<template>
  <q-page class="full-width column items-center tw-my-6">
    <span v-if="errorSubmittingRecap" class="text-red"
      ><strong>{{ t('recap.validationError', 0, { locale: recapLanguage }) }}</strong></span
    >
    <template v-for="page in allPages" :key="`${recapType}-${taskOrStoreId}-${page.value}`">
      <recap-page
        v-show="page.value === selectedPage?.value"
        :ref="(getPageRef as any)"
        :page-id="page.value"
      >
      </recap-page>
    </template>
    <div class="row tw-w-2/3" v-if="pageOptions.length">
      <q-btn v-if="!isFirstPage" push color="primary" @click="selectPage(selectedPageIndex - 1)">
        <span>
          {{ t('recap.page.back', 0, { locale: recapLanguage }) }}
        </span>
      </q-btn>
      <div class="tw-flex-grow"></div>
      <q-btn v-if="!isLastPage" push color="primary" @click="selectPage(selectedPageIndex + 1)">
        <span>
          {{ t('recap.page.next', 0, { locale: recapLanguage }) }}
        </span>
      </q-btn>
      <q-btn
        v-if="isLastPage && props.testingRecap && !previewRecap"
        push
        color="green"
        @click="submitRecap"
      >
        <span>
          {{ t('recap.page.finish', 0, { locale: recapLanguage }) }}
        </span>
      </q-btn>
    </div>
    <div class="row tw-w-2/3 tw-justify-center">
      <q-select
        v-model="languageChoice"
        :label="t('recap.language', 0, { locale: recapLanguage })"
        class="tw-my-3 tw-w-32"
        :options="languageOptions"
      ></q-select>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { LanguageChoice } from '../../i18n'
import { computed, ref } from 'vue'
import RecapPage from './RecapPage.vue'
import { useI18n } from 'vue-i18n'
import { useSharedRecap } from '../../composables/recaps/useRecap'
import { logInfo } from '../../utils'

const props = withDefaults(
  defineProps<{
    recapId: string
    taskOrStoreId: string
    recapType?: 'task' | 'store'
    testingTaskId?: string
    testingRecap?: boolean
    previewRecap?: boolean
  }>(),
  {
    recapType: 'task',
    testingTaskId: undefined,
    previewRecap: false,
  }
)

const { t } = useI18n({ useScope: 'global' })

const {
  getPageRef,
  getRecapResponse,
  resetRecap,
  setUpComposable,
  recapLanguage,
  allPages,
  pageOptions,
  selectedPage,
  selectPage,
  submittingRecap,
  canSubmitRecap,
  errorSubmittingRecap,
} = useSharedRecap()

type LanguageChoiceOption = { label: string; value: LanguageChoice }
const languageChoice = ref<LanguageChoice>(recapLanguage.value)
const languageOptions: LanguageChoiceOption[] = [
  { label: 'English', value: 'en_US' },
  { label: 'Français', value: 'fr_CA' },
  { label: 'Español', value: 'es_MX' },
]

const selectedPageIndex = computed(() =>
  pageOptions.value.findIndex((page) => selectedPage.value?.value === page.value)
)

const isFirstPage = computed(
  () => pageOptions.value.findIndex((page) => page.value === selectedPage.value?.value) === 0
)
const isLastPage = computed(
  () =>
    pageOptions.value.findIndex((page) => page.value === selectedPage.value?.value) ===
    pageOptions.value.length - 1
)

// Recap Submission
async function submitRecap() {
  canSubmitRecap.value = false
  submittingRecap.value = true
  const recapResponse = await getRecapResponse()
  if (recapResponse && typeof recapResponse === 'object' && !('validationError' in recapResponse)) {
    const recapResponseEvent = new CustomEvent<{ response: object }>('recapResponse', {
      detail: {
        response: recapResponse,
      },
    })
    window.dispatchEvent(recapResponseEvent)
  }
}

// Expose the resetRecap function for explicit cleanup
defineExpose({
  resetRecap,
  getRecapResponse,
})

// Set up the recap
setUpComposable(
  props.recapId,
  props.recapType,
  props.taskOrStoreId,
  props.testingRecap,
  props.testingTaskId,
  props.previewRecap
)
  .then(() =>
    selectPage(0).catch((err) => {
      logInfo('error', 'RecapDefinitionLoadingError', 'First recap page failed to select', {
        error: err,
        recapId: props.recapId,
      })
    })
  )
  .catch((err) => {
    logInfo('error', 'RecapDefinitionLoadingError', 'Recap definition failed to load', {
      error: err,
      recapId: props.recapId,
    })
  })
</script>
